import { Box, Chip, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

import { formatEducationLevel } from '../../utils/educationLevels';

import type { PickedSource } from './SourceCards';

interface SearchFilterChipsProps {
  source: PickedSource | null;
  subject: string | null;
  grade: string | null;
  openSet: { id: string; title: string } | null;
  includeDeprecated: boolean;
  onClearSource: () => void;
  onClearSubject: () => void;
  onClearGrade: () => void;
  onClearSet: () => void;
  onToggleDeprecated: () => void;
}

/**
 * Toggle for surfacing deprecated documents. Shared by the search filter row and the browse funnel;
 * off by default so deprecated content is hidden until explicitly requested.
 */
export function DeprecatedToggleChip({
  active,
  onToggle,
}: {
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <Chip
      size="small"
      label="Include older standards"
      icon={active ? <CheckIcon /> : undefined}
      color={active ? 'primary' : 'default'}
      variant={active ? 'filled' : 'outlined'}
      onClick={onToggle}
      sx={{ flexShrink: 0 }}
    />
  );
}

/**
 * Makes the search's implicit funnel filters visible and reversible (Decision 7a). Each active filter
 * is a deletable chip backed by the current browse state; the deprecated toggle drives the search RPC's
 * includeDeprecated param (excluded by default, per Decision 5a).
 */
export function SearchFilterChips({
  source,
  subject,
  grade,
  openSet,
  includeDeprecated,
  onClearSource,
  onClearSubject,
  onClearGrade,
  onClearSet,
  onToggleDeprecated,
}: SearchFilterChipsProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 1.5,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        Filters
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'nowrap' }}>
        {source && (
          <Chip size="small" label={source.title} onDelete={onClearSource} sx={{ flexShrink: 0 }} />
        )}
        {subject && (
          <Chip size="small" label={subject} onDelete={onClearSubject} sx={{ flexShrink: 0 }} />
        )}
        {grade && (
          <Chip
            size="small"
            label={formatEducationLevel(grade)}
            onDelete={onClearGrade}
            sx={{ flexShrink: 0 }}
          />
        )}
        {openSet && (
          <Chip size="small" label={openSet.title} onDelete={onClearSet} sx={{ flexShrink: 0 }} />
        )}
        <DeprecatedToggleChip active={includeDeprecated} onToggle={onToggleDeprecated} />
      </Stack>
    </Box>
  );
}

export default SearchFilterChips;
